import { DataTable, useContainerHeight } from '@components/DataTable';
import { DataTableColumn } from '@components/DataTable/types';
import { GET_USER_SUBSCRIPTION_PLANS_UPERCUT } from '@gql/subscriptions';
import { usePaginatedQuery } from '@hooks/usePaginatedQuery';
import { usePageSize } from '@hooks/usePageSize';
import { Alert, Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import SubscriptionPlansFilter from './SubscriptionPlansFilter';
import { defaultSubscriptionPlansFilters, SubscriptionPlansFilterForm } from './types';

interface SubscriptionPlan {
    _id: string;
    proId: string;
    pro?: {
        _id: string;
        commercialName: string;
        email?: string;
        brandLogo?: {
            url: string;
        };
    };
    status: string;
    type: string;
    startDate: string;
    endDate: string;
    commitmentStartDate: string;
    commitmentEndDate: string;
    durationInterval: string;
    durationIntervalCount: number;
    name: string;
    description: string;
    stripeId?: string;
    createdAt: string;
    price: {
        unitAmount: number;
        currency: string;
        recurring: string;
        metadata: {
            partnerName: string;
        };
    };
}

interface PaginatedResponse {
    getUserSubscriptionPlansUpercut: {
        docs: SubscriptionPlan[];
        meta: {
            totalDocs: number;
            limit: number;
            page: number;
            totalPages: number;
            nextPage: number | null;
            prevPage: number | null;
        };
    };
}

interface SubscriptionPlansTableProps {
    fixedProId?: string;
    showTitle?: boolean;
}

const statusColorMap: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
    active: 'success',
    trialing: 'info',
    canceled: 'error',
    past_due: 'warning',
    unpaid: 'error',
};

const toFilterDate = (value: Date | string | null | undefined) => {
    if (!value) return null;
    if (value instanceof Date) {
        return value.toISOString();
    }
    return value;
};

const buildDateRange = (start?: Date | string | null, end?: Date | string | null) => {
    const range: Record<string, string> = {};

    const startValue = toFilterDate(start);
    const endValue = toFilterDate(end);

    if (startValue) {
        range.start = startValue;
    }

    if (endValue) {
        range.end = endValue;
    }

    return Object.keys(range).length ? range : undefined;
};

const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const formatPrice = (unitAmount: number, currency: string) => {
    if (!unitAmount && unitAmount !== 0) return '-';
    return `${(unitAmount / 100).toFixed(2)} ${(currency || 'EUR').toUpperCase()}`;
};

const formatCycle = (interval: string) => {
    if (!interval) return '-';
    if (interval === 'year') return 'Annuel';
    if (interval === 'month') return 'Mensuel';
    return interval;
};

const formatType = (type: string) => {
    if (!type) return '-';
    if (type === 'recurring') return 'Récurrent';
    if (type === 'one_time_commitment') return 'Sans engagement';
    return type;
};

const SubscriptionPlansTable = ({ fixedProId, showTitle = true }: SubscriptionPlansTableProps) => {
    const { containerRef, containerHeight } = useContainerHeight();
    const [page, setPage] = useState(0);
    const [query, setQuery] = useState('');
    const storageKey = fixedProId ? 'company-subscription-plans-page-size' : 'upercut-subscription-plans-page-size';
    const { pageSize, storePageSize } = usePageSize({ storageKey });

    const [appliedFilters, setAppliedFilters] = useState<SubscriptionPlansFilterForm>({
        ...defaultSubscriptionPlansFilters,
        proId: fixedProId || '',
    });

    const queryFilter = useMemo(() => {
        const effectiveProId = fixedProId || appliedFilters.proId.trim();

        const filter: Record<string, unknown> = {};

        if (effectiveProId) {
            filter.proId = effectiveProId;
        }

        if (appliedFilters.status) {
            filter.status = [appliedFilters.status];
        }

        if (appliedFilters.partnerName.trim()) {
            filter.partnerName = appliedFilters.partnerName.trim();
        }

        const createdAt = buildDateRange(appliedFilters.createdAt.start, appliedFilters.createdAt.end);
        if (createdAt) {
            filter.createdAt = createdAt;
        }

        const commitmentStartDate = buildDateRange(
            appliedFilters.commitmentStartDate.start,
            appliedFilters.commitmentStartDate.end,
        );
        if (commitmentStartDate) {
            filter.commitmentStartDate = commitmentStartDate;
        }

        const commitmentEndDate = buildDateRange(
            appliedFilters.commitmentEndDate.start,
            appliedFilters.commitmentEndDate.end,
        );
        if (commitmentEndDate) {
            filter.commitmentEndDate = commitmentEndDate;
        }

        return Object.keys(filter).length ? filter : undefined;
    }, [appliedFilters, fixedProId]);

    const { rows, totalDocs, loading, error, loadMore, isLoadingMore } = usePaginatedQuery<SubscriptionPlan, PaginatedResponse>({
        query: GET_USER_SUBSCRIPTION_PLANS_UPERCUT,
        variables: {
            query: query.trim() || undefined,
            filter: queryFilter,
        },
        page,
        pageSize,
        baseLimit: pageSize,
        paginationMode: 'page',
        extractResult: (data) => ({
            docs: data.getUserSubscriptionPlansUpercut?.docs ?? [],
            totalDocs: data.getUserSubscriptionPlansUpercut?.meta?.totalDocs ?? 0,
            meta: data.getUserSubscriptionPlansUpercut?.meta ?? {
                totalDocs: 0,
                limit: pageSize,
                page: 1,
                totalPages: 0,
                nextPage: null,
                prevPage: null,
            },
        }),
    });

    const columns = useMemo<DataTableColumn<SubscriptionPlan>[]>(() => {
        const baseColumns: DataTableColumn<SubscriptionPlan>[] = [
            {
                id: 'name',
                label: 'Plan',
                sticky: true,
                minWidth: 180,
                renderCell: ({ row }) => (
                    <Stack spacing={0.5}>
                        {row.stripeId ? (
                            <Typography
                                variant="body2"
                                fontWeight={500}
                                component="a"
                                href={`${process.env.REACT_APP_ENV === 'development' ? 'https://dashboard.stripe.com/acct_1JTmJWEwCdWY5dVt/test/subscriptions' : 'https://dashboard.stripe.com/acct_1JTmJWEwCdWY5dVt/subscriptions'}/${row.stripeId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                {row.name || '-'}
                            </Typography>
                        ) : (
                            <Typography variant="body2" fontWeight={500}>
                                {row.name || '-'}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {formatPrice(row.price?.unitAmount, row.price?.currency)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                •
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {formatCycle(row.durationInterval)}
                            </Typography>
                        </Box>
                    </Stack>
                ),
            },
            {
                id: 'status',
                label: 'Statut',
                minWidth: 120,
                renderCell: ({ row }) => (
                    <Chip
                        label={row.status || '-'}
                        size="small"
                        color={statusColorMap[row.status] || 'default'}
                        variant="outlined"
                    />
                ),
            },
            {
                id: 'type',
                label: 'Type',
                minWidth: 130,
                renderCell: ({ row }) => formatType(row.type),
            },
            {
                id: 'partner',
                label: 'Partenaire',
                minWidth: 150,
                renderCell: ({ row }) => row.price?.metadata?.partnerName || '-',
            },
            {
                id: 'createdAt',
                label: 'Créé le',
                minWidth: 120,
                renderCell: ({ row }) => formatDate(row.createdAt),
            },
            {
                id: 'startDate',
                label: 'Date de début',
                minWidth: 130,
                renderCell: ({ row }) => formatDate(row.startDate),
            },
            {
                id: 'endDate',
                label: 'Date de fin',
                minWidth: 130,
                renderCell: ({ row }) => formatDate(row.endDate),
            },
            {
                id: 'commitmentStartDate',
                label: "Début d'engagement",
                minWidth: 160,
                renderCell: ({ row }) => formatDate(row.commitmentStartDate),
            },
            {
                id: 'commitmentEndDate',
                label: "Fin d'engagement",
                minWidth: 160,
                renderCell: ({ row }) => formatDate(row.commitmentEndDate),
            },
        ];

        if (!fixedProId) {
            baseColumns.splice(1, 0, {
                id: 'pro',
                label: 'Entreprise',
                minWidth: 280,
                renderCell: ({ row }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                            src={row.pro?.brandLogo?.url ?? '/default-logo.png'}
                            alt={row.pro?.commercialName}
                            sx={{ width: 32, height: 32 }}
                        >
                            {row.pro?.commercialName?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight={500}>
                                {row.pro?.commercialName || '-'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {row.pro?.email || '-'}
                            </Typography>
                        </Stack>
                    </Box>
                ),
            });
        }

        return baseColumns;
    }, [fixedProId]);

    const onFilterChange = (filter: SubscriptionPlansFilterForm) => {
        setAppliedFilters({
            ...filter,
            proId: fixedProId || filter.proId,
        });
        setPage(0);
    };

    const onQueryChange = useCallback((nextQuery: string) => {
        let hasChanged = false;

        setQuery((previousQuery) => {
            if (previousQuery === nextQuery) {
                return previousQuery;
            }

            hasChanged = true;
            return nextQuery;
        });

        if (hasChanged) {
            setPage(0);
        }
    }, []);

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
            {showTitle && (
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Plans d'abonnement
                </Typography>
            )}

            <SubscriptionPlansFilter
                filter={appliedFilters}
                fixedProId={fixedProId}
                totalDocs={totalDocs}
                query={query}
                onFilterChange={onFilterChange}
                onQueryChange={onQueryChange}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Impossible de charger les abonnements.
                </Alert>
            )}
            <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <DataTable
                    stickyHeader
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    height={containerHeight || undefined}
                    pagination={{
                        currentPage: page + 1,
                        pageSize,
                        totalRows: totalDocs,
                        pageSizeOptions: [10, 20, 50, 100],
                        onPageChange: (newPage) => setPage(newPage - 1),
                        onPageSizeChange: (newSize) => {
                            storePageSize(newSize);
                            setPage(0);
                        },
                    }}
                    onScrollEnd={loadMore}
                    isLoadingMore={isLoadingMore}
                />
            </Box>
        </Box>
    );
};

export default SubscriptionPlansTable;
